import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useParams } from 'react-router-dom';
import { get as _get } from 'lodash';
import axios from 'axios';
import { ProfileData, TabType, /*MapIntelligence, UserCoords,*/ SavedProfile, CommentType, SocialPost } from './types';
import {
  generateProfileData,
  //generateSocialComment,
  //getAddressIntelligence,
} from './services/geminiService';
import { API_BASE_URL } from './config';

// Layout Components
import Navigation from './components/layout/Navigation';
import ProfileHeader from './components/layout/ProfileHeader';
import TabNavigation from './components/layout/TabNavigation';

// Tab Components
import GeneralTab from './components/tabs/GeneralTab';
import PersonalityTab from './components/tabs/PersonalityTab';
import OutreachTab from './components/tabs/OutreachTab';
import SocialTab from './components/tabs/SocialTab';

// Modal Components
import ProfileGeneratorModal from './components/modals/ProfileGeneratorModal';

// ---------------------------------------------------------------------------
// PersonalityPage — fetches profile by :id from the API
// ---------------------------------------------------------------------------
const PersonalityPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  // Core State
  const [activeTab, setActiveTab] = useState<TabType>('general');
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [savedProfiles, setSavedProfiles] = useState<SavedProfile[]>([]);

  // API loading / error state
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);

  // Generator State
  const [showGenerator, setShowGenerator] = useState(false);
  const [isGeneratingProfile, setIsGeneratingProfile] = useState(false);

  // Maps Intelligence State
  //const [mapsIntel, setMapsIntel] = useState<MapIntelligence | null>(null);
  //const [isLoadingMaps, setIsLoadingMaps] = useState(false);
  //const [userCoords, setUserCoords] = useState<UserCoords | undefined>(undefined);

  // Outreach State
  const [outreachDraft, setOutreachDraft] = useState('');
  const [isGeneratingOutreach, setIsGeneratingOutreach] = useState(false);

  // Social State
  const [selectedPostIdx, setSelectedPostIdx] = useState<number | null>(null);
  const [socialComment, setSocialComment] = useState('');
  const [isGeneratingSocial, setIsGeneratingSocial] = useState(false);

  // Personality Animation State
  const [barsAnimated, setBarsAnimated] = useState(false);

  // ── Fetch profile from API ────────────────────────────────────────────────
  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    setApiError(null);

    axios
      .get(`${API_BASE_URL}/profile/v4/card/${id}`)
      .then((res) => {
        const responseData = _get(res, 'data.responseData', null);
        if (responseData && Object.keys(responseData).length > 0) {
          setProfile(responseData as ProfileData);
        } else {
          setApiError('No profile data found for this ID.');
        }
      })
      .catch((err) => {
        console.error('API fetch error:', err);
        setApiError('Failed to load profile. Please try again later.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id]);

  // ── Animate OCEAN bars when personality tab is active ─────────────────────
  useEffect(() => {
    if (activeTab === 'personality') {
      const timer = setTimeout(() => setBarsAnimated(true), 200);
      return () => clearTimeout(timer);
    } else {
      setBarsAnimated(false);
    }
  }, [activeTab]);

  // ── Request user location for enhanced grounding ──────────────────────────
  /*useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserCoords({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          });
        },
        (err) => console.warn('Geolocation access denied:', err.message)
      );
    }
  }, []);

  // ── Load Maps Grounding data when profile address changes ─────────────────
  useEffect(() => {
    if (!profile) return;
    const address = _get(profile, 'address', '');
    if (address) {
      setIsLoadingMaps(true);
      getAddressIntelligence(address, userCoords)
        .then((data) => {
          setMapsIntel(data);
          setIsLoadingMaps(false);
        })
        .catch((error) => {
          console.error('Maps intelligence error:', error);
          setIsLoadingMaps(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.address, userCoords]);*/

  // ── Profile Generation ────────────────────────────────────────────────────
  const handleGenerateProfile = useCallback(
    async (
      name: string,
      title: string,
      industry: string,
      companySize: string,
      segment: string,
      objective: string
    ) => {
      setIsGeneratingProfile(true);
      try {
        const newProfile = await generateProfileData(
          name,
          title,
          industry,
          companySize,
          segment,
          objective
        );
        setProfile(newProfile);
        setShowGenerator(false);
        setActiveTab('general');
      } catch (error) {
        console.error('Profile generation error:', error);
        alert('Failed to generate profile. Check API key.');
      } finally {
        setIsGeneratingProfile(false);
      }
    },
    []
  );

  // ── Save to Library ───────────────────────────────────────────────────────
  const handleSaveToLibrary = useCallback(() => {
    if (!profile) return;
    const name = _get(profile, 'name', '');
    const title = _get(profile, 'title', '');
    const ocean = _get(profile, 'ocean');

    if (!ocean) {
      alert('Profile data is incomplete.');
      return;
    }

    const newEntry: SavedProfile = { name, title, ocean };
    setSavedProfiles((prev) => [...prev, newEntry]);
    alert(`${name} has been saved to your library.`);
  }, [profile]);

  // ── Outreach Draft Generation ─────────────────────────────────────────────
  const handleGenerateOutreach = useCallback(
    async (channel: string, tone: string, objective: string, extraContext: string, painPoints: string[], whitepaperUrl: string) => {
      if (!profile) return;
      setIsGeneratingOutreach(true);
      try {
        const payload = {
          name: _get(profile, 'name', ''),
          title: _get(profile, 'title', ''),
          archetype: _get(profile, 'archetype', ''),
          communicationStyle: _get(profile, 'communication.description', ''),
          objective: objective,
          extraContext: extraContext,
          channel: channel,
          tone: tone,
          insightsClosingSecret: _get(profile, 'insights.closingSecret', ''),
          emailSalutation: `${_get(profile, 'emailGuidelines.salutation', '')}, ${_get(profile, 'emailGuidelines.greeting', '')}`,
          emailGreeting: _get(profile, 'emailGuidelines.greeting', ''),
          emailLength: _get(profile, 'emailGuidelines.length', ''),
          painPoints: painPoints,
          whitepaperUrl: whitepaperUrl || undefined,
        };
        const res = await axios.post(
          `${API_BASE_URL}/profile/v3/generate/outreach-draft`,
          payload
        );
        const isSuccess = _get(res, 'data.responseStatus', false);
        if (isSuccess) {
          setOutreachDraft(_get(res, 'data.responseData.response', 'Failed to generate draft.'));
        } else {
          setOutreachDraft(_get(res, 'data.responseMessage', 'Failed to generate draft.'));
        }
      } catch (error) {
        console.error('Outreach generation error:', error);
        setOutreachDraft('Error generating outreach draft.');
      } finally {
        setIsGeneratingOutreach(false);
      }
    },
    [profile]
  );

  // ── Social Comment Generation ─────────────────────────────────────────────
  const handleGenerateSocial = useCallback(
    async (idx: number, type: CommentType) => {
      if (!profile) return;
      const posts = _get(profile, 'socialActivity.posts', []);
      const post = posts[idx];
      if (!post) return;

      const postContent = _get(post, 'content', '');
      if (!postContent) return;

      setSelectedPostIdx(idx);
      setIsGeneratingSocial(true);
      try {
        const payload = {
            "name": _get(profile, 'name', ''),
            "communicationStyle": _get(profile, "communication.description", ""),
            "postContent": postContent,
            "commentType": type
          };
        const res = await axios.post(
          `${API_BASE_URL}/profile/v3/generate/social-comment`,
          payload
        );
        const isSuccess = _get(res, 'data.responseStatus', false);
        if (isSuccess) {
          setSocialComment(_get(res, 'data.responseData.response', 'Failed to generate draft.'));
        } else {
          setSocialComment(_get(res, 'data.responseMessage', 'Failed to generate draft.'));
        }
      } catch (error) {
        console.error('Social comment generation error:', error);
        setSocialComment('Error generating comment.');
      } finally {
        setIsGeneratingSocial(false);
      }
    },
    [profile]
  );

  // ── Utility Functions ─────────────────────────────────────────────────────
  const copyToClipboard = useCallback((text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  }, []);

  const sharePost = useCallback(
    (post: SocialPost) => {
      if (!profile) return;
      const profileName = _get(profile, 'name', '');
      const content = _get(post, 'content', '');
      const text = `Check out this insight from ${profileName}: "${content}" via Rethink Demand Intelligence`;

      if (navigator.share) {
        navigator
          .share({
            title: 'Social Insight',
            text: text,
            url: window.location.href,
          })
          .catch(console.error);
      } else {
        copyToClipboard(text);
      }
    },
    [profile, copyToClipboard]
  );

  // ── Loading state ─────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div
            style={{
              width: 48,
              height: 48,
              border: '4px solid #e5e7eb',
              borderTop: '4px solid #FA6284',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
              margin: '0 auto 16px',
            }}
          />
          <p style={{ color: '#6b7280', fontSize: 14, fontWeight: 600 }}>Loading Profile…</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // ── Error / no data state ─────────────────────────────────────────────────
  if (apiError || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div style={{ textAlign: 'center', padding: '60px 20px', maxWidth: 480 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
            No Data Found
          </h2>
          <p style={{ color: '#6b7280', fontSize: 15, lineHeight: 1.6 }}>
            {apiError || 'The profile you are looking for could not be found.'}
          </p>
        </div>
      </div>
    );
  }

  // ── Main UI ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen relative">
      <Navigation
        onGenerateClick={() => setShowGenerator(true)}
        onLogoClick={() => setActiveTab('general')}
      />

      <main className="max-w-7xl mx-auto px-6 py-10">
        <ProfileHeader profile={profile} />

        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />

        {activeTab === 'general' && (
          <GeneralTab
            profile={profile}
            mapsIntel={null}
            isLoadingMaps={false}
            savedProfiles={savedProfiles}
          />
        )}

        {activeTab === 'personality' && (
          <PersonalityTab
            profile={profile}
            barsAnimated={barsAnimated}
            onSaveToLibrary={handleSaveToLibrary}
          />
        )}

        {activeTab === 'outreach' && (
          <OutreachTab
            profile={profile}
            onGenerateDraft={handleGenerateOutreach}
            isGenerating={isGeneratingOutreach}
            draft={outreachDraft}
            onCopy={copyToClipboard}
          />
        )}

        {activeTab === 'social' && (
          <SocialTab
            profile={profile}
            selectedPostIdx={selectedPostIdx}
            onSelectPost={setSelectedPostIdx}
            onCopy={copyToClipboard}
            onShare={sharePost}
            onGenerateComment={handleGenerateSocial}
            socialComment={socialComment}
            setSocialComment={setSocialComment}
            isGenerating={isGeneratingSocial}
          />
        )}
      </main>

      <ProfileGeneratorModal
        isOpen={showGenerator}
        onClose={() => setShowGenerator(false)}
        onGenerate={handleGenerateProfile}
        isGenerating={isGeneratingProfile}
      />

      <footer className="py-12 text-center text-brand-grey02 text-[10px] font-black uppercase tracking-[0.3em] border-t border-brand-grey01 bg-white no-print">
        &copy; 2026 Rethink Demand. Precision Marketing Intelligence.
      </footer>
    </div>
  );
};

// ---------------------------------------------------------------------------
// NotFoundPage — shown for any unmatched routes
// ---------------------------------------------------------------------------
const NotFoundPage: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div style={{ textAlign: 'center', padding: '60px 20px', maxWidth: 480 }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>🚫</div>
      <h2 style={{ fontSize: 24, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
        No Data Found
      </h2>
      <p style={{ color: '#6b7280', fontSize: 15, lineHeight: 1.6 }}>
        The page you are looking for does not exist. Please check the URL and try again.
      </p>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// App — root router definition
// ---------------------------------------------------------------------------
const App: React.FC = () => {
  return (
    <Routes>
      {/* Primary route: /personality/:id */}
      <Route path="/personality/:id" element={<PersonalityPage />} />

      {/* Redirect bare root to not-found or keep as not-found */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default App;
